import { useState, useCallback, useEffect } from 'react';
import {
  Card,
  CardHeader,
  Button,
  Select,
  Input,
  Badge,
  Pagination,
  LoadingSkeleton,
} from '@nexora/design-system';
import { TaxRateFormModal } from './TaxRateFormModal';
import {
  useTaxRates,
  useCreateTaxRate,
  useUpdateTaxRate,
  useDeleteTaxRate,
  type TaxRate,
  type TaxRateInput,
} from './useTaxRates';
import { formatDate } from '../../../shared/utils/format';

const IMPOSTO_FILTER_OPTIONS = [
  { value: 'ALL', label: 'Todos os impostos' },
  { value: 'ICMS', label: 'ICMS' },
  { value: 'ISS', label: 'ISS' },
  { value: 'PIS', label: 'PIS' },
  { value: 'COFINS', label: 'COFINS' },
  { value: 'IPI', label: 'IPI' },
  { value: 'IBS', label: 'IBS' },
  { value: 'CBS', label: 'CBS' },
];

const UFS = [
  'AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS',
  'MT', 'PA', 'PB', 'PE', 'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC',
  'SE', 'SP', 'TO', 'EX',
];

/** Percentual com duas casas, como a alíquota é publicada. */
function formatRate(v: number): string {
  return `${v.toFixed(2).replace('.', ',')}%`;
}

/** "SP → RJ"; lado sem UF vale para qualquer um. */
function scenarioOf(tr: TaxRate): string {
  if (!tr.ufOrigem && !tr.ufDestino) return 'qualquer';
  return `${tr.ufOrigem ?? 'qualquer'} → ${tr.ufDestino ?? 'qualquer'}`;
}

type RateStatus = 'vigente' | 'agendada' | 'vencida';

/**
 * Situação da alíquota.
 *
 * `isInForce` respondia sim ou não. Quem olha a tela precisa saber se o "não"
 * é porque a vigência ainda não começou (criar é cedo demais) ou porque já
 * venceu (precisa renovar) — são ações opostas.
 */
function rateStatus(tr: TaxRate): RateStatus {
  const hoje = new Date();
  const inicio = new Date(tr.effectiveSince);
  if (!Number.isNaN(inicio.getTime()) && inicio > hoje) return 'agendada';
  if (tr.effectiveUntil) {
    const fim = new Date(tr.effectiveUntil);
    if (!Number.isNaN(fim.getTime()) && fim < hoje) return 'vencida';
  }
  return 'vigente';
}

const STATUS_LABEL: Record<RateStatus, string> = {
  vigente: 'Em vigor',
  agendada: 'Agendada',
  vencida: 'Vencida',
};

const STATUS_VARIANT: Record<RateStatus, 'success' | 'info' | 'warning'> = {
  vigente: 'success',
  agendada: 'info',
  vencida: 'warning',
};

export function TaxRatesPage(): JSX.Element {
  const [page, setPage] = useState(1);
  const [impostoFilter, setImpostoFilter] = useState('ALL');
  const [ufOrigemFilter, setUfOrigemFilter] = useState('');
  const [ufDestinoFilter, setUfDestinoFilter] = useState('');
  const [ncmFilter, setNcmFilter] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTaxRate, setEditingTaxRate] = useState<TaxRate | undefined>(undefined);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastVariant, setToastVariant] = useState<'success' | 'error'>('success');

  /* Os quatro filtros vao ao servidor — `QueryTaxRateDto` sempre os aceitou e
     a tela usava apenas o primeiro. */
  const { data, isLoading, error, refetch } = useTaxRates({
    taxType: impostoFilter,
    ufOrigem: ufOrigemFilter || undefined,
    ufDestino: ufDestinoFilter || undefined,
    ncmPrefix: ncmFilter || undefined,
    page,
    limit: 20,
  });

  const createMutation = useCreateTaxRate();
  const updateMutation = useUpdateTaxRate();
  const deleteMutation = useDeleteTaxRate();

  // Auto-dismiss toast
  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => setToastMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [toastMessage]);

  const showToast = useCallback((message: string, variant: 'success' | 'error') => {
    setToastMessage(message);
    setToastVariant(variant);
  }, []);

  const handleCreate = useCallback(
    async (input: TaxRateInput) => {
      try {
        await createMutation.mutateAsync(input);
        showToast('Alíquota criada com sucesso.', 'success');
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Erro ao criar aliquota.';
        showToast(msg, 'error');
        throw err; // re-throw so modal shows error too
      }
    },
    [createMutation, showToast],
  );

  const handleUpdate = useCallback(
    async (input: TaxRateInput) => {
      if (!editingTaxRate) return;
      try {
        await updateMutation.mutateAsync({ id: editingTaxRate.id, ...input });
        showToast('Alíquota atualizada com sucesso.', 'success');
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Erro ao atualizar aliquota.';
        showToast(msg, 'error');
        throw err;
      }
    },
    [editingTaxRate, updateMutation, showToast],
  );

  const handleEdit = useCallback((taxRate: TaxRate) => {
    setEditingTaxRate(taxRate);
    setModalOpen(true);
  }, []);

  const handleNew = useCallback(() => {
    setEditingTaxRate(undefined);
    setModalOpen(true);
  }, []);

  const handleDelete = useCallback(
    async (id: string) => {
      try {
        await deleteMutation.mutateAsync(id);
        showToast('Alíquota excluida com sucesso.', 'success');
        setDeleteConfirmId(null);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Erro ao excluir aliquota.';
        showToast(msg, 'error');
        setDeleteConfirmId(null);
      }
    },
    [deleteMutation, showToast],
  );

  const handleFilterChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setImpostoFilter(e.target.value);
    setPage(1);
  }, []);

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    setEditingTaxRate(undefined);
  }, []);

  const totalPages = data?.totalPages ?? 1;
  const taxRates = data?.data ?? [];

  return (
    <div style={{ padding: '1.5rem' }}>
      {/* Page header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '1.5rem',
        }}
      >
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">
            Configuração de Tributos
          </h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Alíquotas por tributo, cenário e vigência — a base que o motor fiscal consulta.
          </p>
        </div>
        <Button variant="primary" onClick={handleNew}>
          + Nova Alíquota
        </Button>
      </div>

      {/* Filter */}
      <Card padding="1rem" variant="outlined" className="filter-card">
        <div className="rate-filters">
          <div className="rate-filter">
            <Select label="Tributo" value={impostoFilter} onChange={handleFilterChange}>
              {IMPOSTO_FILTER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </div>
          <div className="rate-filter">
            <Select
              label="UF de origem"
              value={ufOrigemFilter}
              onChange={(e) => {
                setUfOrigemFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">Todas</option>
              {UFS.map((uf) => (
                <option key={uf} value={uf}>{uf}</option>
              ))}
            </Select>
          </div>
          <div className="rate-filter">
            <Select
              label="UF de destino"
              value={ufDestinoFilter}
              onChange={(e) => {
                setUfDestinoFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">Todas</option>
              {UFS.map((uf) => (
                <option key={uf} value={uf}>{uf}</option>
              ))}
            </Select>
          </div>
          <div className="rate-filter">
            <Input
              label="Prefixo NCM"
              placeholder="Ex.: 8471"
              value={ncmFilter}
              onChange={(e) => {
                setNcmFilter(e.target.value.replace(/\D/g, ''));
                setPage(1);
              }}
            />
          </div>
          {/* O servidor mantém, no filtro de UF, as alíquotas SEM UF definida:
              elas valem para qualquer cenário. Dizer isso evita a leitura de
              que o filtro não funcionou. */}
          <p className="rate-filter__note">
            Filtrar por UF mantém também as alíquotas sem UF definida — elas
            valem para qualquer cenário.
          </p>
        </div>
      </Card>

      {/* Content */}
      <Card variant="outlined" padding="0">
        <CardHeader
          className="px-md pt-md"
          action={
            <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">
              {data?.total ?? 0} {(data?.total ?? 0) === 1 ? 'alíquota' : 'alíquotas'}
            </span>
          }
        >
          <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Alíquotas</h3>
        </CardHeader>
        {/* Loading state */}
        {isLoading && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '3rem',
              gap: '0.75rem',
              color: '#43474e',
            }}
          >
            <LoadingSkeleton />  {/* loading indicator */}
            <span style={{ fontSize: '0.875rem' }}>Carregando aliquotas...</span>
          </div>
        )}

        {/* Error state */}
        {error && !isLoading && (
          <div
            style={{
              padding: '3rem 2rem',
              textAlign: 'center',
              color: '#ba1a1a',
            }}
          >
            <p style={{ fontWeight: 600, margin: '0 0 0.5rem' }}>Erro ao carregar aliquotas</p>
            <p style={{ fontSize: '0.875rem', margin: '0 0 1rem', color: '#74777f' }}>
              {error instanceof Error ? error.message : 'Erro desconhecido'}
            </p>
            <Button variant="primary" onClick={() => refetch()}>
              Tentar novamente
            </Button>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !error && taxRates.length === 0 && (
          <div
            style={{
              padding: '3rem 2rem',
              textAlign: 'center',
              color: '#74777f',
            }}
          >
            <p style={{ margin: '0 0 1rem', fontSize: '0.875rem' }}>
              Nenhuma aliquota encontrada.
            </p>
            <Button variant="outline" size="sm" onClick={handleNew}>
              Criar primeira aliquota
            </Button>
          </div>
        )}

        {/* Table */}
        {!isLoading && !error && taxRates.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '0.875rem',
              }}
            >
              <thead>
                <tr>
                  {['Tributo', 'Alíquota', 'Cenário (UF)', 'Aplicação', 'Ajustes', 'Vigência', 'Situação', 'Prio.', 'Ações'].map(
                    (h) => (
                      <th
                        key={h}
                        style={{
                          textAlign: 'left',
                          padding: '0.75rem 1rem',
                          background: '#f2f4f6',
                          color: '#43474e',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          borderBottom: '2px solid #e0e3e5',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {taxRates.map((tr) => (
                  <tr
                    key={tr.id}
                    style={{
                      transition: 'background 0.15s ease',
                    }}
                    className="tax-rates-row"
                  >
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0' }}>
                      <Badge variant="info" size="sm">
                        {tr.taxType}
                      </Badge>
                    </td>
                    <td className="rate-cell rate-cell--num">{formatRate(tr.aliquota)}</td>
                    <td className="rate-cell rate-cell--mono">
                      {/* Antes so `ufOrigem` aparecia: uma aliquota SP->RJ e
                          outra SP->MG ficavam identicas na tela. */}
                      {scenarioOf(tr)}
                    </td>
                    <td className="rate-cell">
                      <span className="rate-scope">
                        {tr.ncmPrefix ? <span className="rate-tag">NCM {tr.ncmPrefix}</span> : null}
                        {tr.municipioIbge ? <span className="rate-tag">IBGE {tr.municipioIbge}</span> : null}
                        {tr.regimeDestinatario ? (
                          <span className="rate-tag">{tr.regimeDestinatario}</span>
                        ) : null}
                        {!tr.ncmPrefix && !tr.municipioIbge && !tr.regimeDestinatario ? (
                          <span className="rate-muted">qualquer</span>
                        ) : null}
                      </span>
                    </td>
                    <td className="rate-cell">
                      {/* Reducao de base e MVA sao o que fazem duas aliquotas de
                          mesmo percentual calcularem imposto diferente. Sem
                          estas colunas, as duas linhas pareciam a mesma coisa. */}
                      <span className="rate-scope">
                        {tr.reducao !== null && tr.reducao !== undefined ? (
                          <span className="rate-tag rate-tag--adj">red. {formatRate(tr.reducao)}</span>
                        ) : null}
                        {tr.mva !== null && tr.mva !== undefined ? (
                          <span className="rate-tag rate-tag--adj">MVA {formatRate(tr.mva)}</span>
                        ) : null}
                        {(tr.reducao === null || tr.reducao === undefined) &&
                        (tr.mva === null || tr.mva === undefined) ? (
                          <span className="rate-muted">—</span>
                        ) : null}
                      </span>
                    </td>
                    <td className="rate-cell rate-cell--mono">
                      {formatDate(tr.effectiveSince)}
                      {tr.effectiveUntil ? ` – ${formatDate(tr.effectiveUntil)}` : ' →'}
                    </td>
                    <td className="rate-cell">
                      {/* Aliquota nao tem interruptor: tem vigencia. Separar
                          "ainda nao comecou" de "ja venceu" e o que diz ao
                          usuario se ele precisa criar ou renovar. */}
                      <Badge variant={STATUS_VARIANT[rateStatus(tr)]} size="sm" dot>
                        {STATUS_LABEL[rateStatus(tr)]}
                      </Badge>
                    </td>
                    <td className="rate-cell rate-cell--num">
                      {tr.rulePriority}
                      {tr.source ? <small className="rate-source">{tr.source}</small> : null}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(tr)}
                        >
                          Editar
                        </Button>
                        {deleteConfirmId === tr.id ? (
                          <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                            <span style={{ fontSize: '0.75rem', color: '#ba1a1a' }}>Excluir?</span>
                            <Button
                              variant="danger"
                              size="sm"
                              loading={deleteMutation.isPending}
                              onClick={() => handleDelete(tr.id)}
                            >
                              Sim
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeleteConfirmId(null)}
                            >
                              Não
                            </Button>
                          </div>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteConfirmId(tr.id)}
                            style={{ color: '#ba1a1a' }}
                          >
                            Excluir
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!isLoading && !error && totalPages > 1 && (
          <div
            style={{
              padding: '1rem',
              borderTop: '1px solid #eceef0',
            }}
          >
            <Pagination
              page={page}
              totalPages={totalPages}
              onChange={(p) => setPage(p)}
            />
          </div>
        )}
      </Card>

      {/* Toast notification */}
      {toastMessage && (
        <div
          role="alert"
          style={{
            position: 'fixed',
            bottom: '1.5rem',
            right: '1.5rem',
            padding: '0.75rem 1rem',
            borderRadius: '8px',
            background: toastVariant === 'success' ? '#d3f5d6' : '#ffdad6',
            border: `1px solid ${toastVariant === 'success' ? '#b6ebbb' : '#ffb4ab'}`,
            color: toastVariant === 'success' ? '#2e7d32' : '#ba1a1a',
            fontSize: '0.875rem',
            fontWeight: 500,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            zIndex: 10000,
            maxWidth: '400px',
            animation: 'toast-slide-in 0.3s ease',
          }}
        >
          {toastMessage}
          <button
            onClick={() => setToastMessage(null)}
            aria-label="Fechar"
            style={{
              marginLeft: '0.75rem',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'inherit',
              fontSize: '1rem',
              padding: '0',
              lineHeight: 1,
            }}
          >
            &times;
          </button>
        </div>
      )}

      {/* Form modal */}
      <TaxRateFormModal
        isOpen={modalOpen}
        onClose={handleModalClose}
        taxRate={editingTaxRate}
        onSubmit={editingTaxRate ? handleUpdate : handleCreate}
      />

      <style>{`
        .rate-filters { display: flex; gap: 1rem; align-items: flex-end; flex-wrap: wrap; }
        .rate-filter { min-width: 150px; }
        .rate-filter__note { flex-basis: 100%; margin: 0.25rem 0 0; font-size: 0.75rem; color: #74777f; }

        .rate-cell { padding: 0.625rem 0.75rem; border-bottom: 1px solid #eceef0; font-size: 0.875rem; color: #191c1e; vertical-align: middle; }
        .rate-cell--num { text-align: right; font-family: 'JetBrains Mono', ui-monospace, monospace; font-variant-numeric: tabular-nums; white-space: nowrap; }
        .rate-cell--mono { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; white-space: nowrap; }
        .rate-scope { display: inline-flex; gap: 0.25rem; flex-wrap: wrap; }
        .rate-tag { padding: 1px 6px; background: #eceef0; color: #43474e; border-radius: 2px; font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 10px; white-space: nowrap; }
        .rate-tag--adj { background: #ffddba; color: #633f0f; }
        .rate-muted { color: #74777f; }
        .rate-source { display: block; font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 9px; letter-spacing: 0.04em; color: #74777f; }

        .tax-rates-row:hover td {
          background: #eceef0;
        }
        .filter-card { margin-bottom: 1rem; }
        @keyframes toast-slide-in {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
